/**
 * The two viewport queries every input block asks, asked once.
 *
 * Both hooks were declared privately in all eleven block files — byte-identical in every one,
 * verified by hashing the function bodies. `useIsPhoneLandscape`'s original comment argued that
 * each block redeclaring its own media query "follows that established convention"; the convention
 * was really just eleven copies of the same six lines, and a query string that drifted in one file
 * would give that block a different idea of what a phone is with nothing to catch it.
 *
 * Kept as hooks rather than a context: they are read during render by blocks that may not share an
 * ancestor (a collapsed card on the canvas, a portaled full-screen editor), and `matchMedia` is
 * cheap enough that a provider would buy nothing.
 */
import { useEffect, useState } from 'react';

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/**
 * What counts as a phone, in one place.
 *
 * The height clause is there for a real case — a phone in landscape is wide and short, and width
 * alone misses it — but on its own it also caught a short DESKTOP window: devtools open, or a split
 * screen. That window then got the full-screen block editor, the phone's two-pipeline undo model,
 * and a keyboard to drive the wrong one with. Requiring a coarse pointer separates them: a phone in
 * landscape has one, a short desktop window does not.
 *
 * Exported because three other files had each written this string out again — the drift this
 * module's own comment warns about, which had already happened by the time anyone looked.
 */
export const PHONE_QUERY = '(max-width: 767px), ((max-height: 599px) and (pointer: coarse))';

/** Phone-sized in either orientation — the switch between the desktop card and the phone layouts. */
export function useIsPhone(): boolean {
  return useMediaQuery(PHONE_QUERY);
}

/**
 * Short and wide, which is NOT the same question as "a phone held sideways" — see below.
 *
 * Exported for the one caller that needs the raw geometry. Everything that means "phone in
 * landscape" wants `useIsPhoneLandscape`.
 */
export const SHORT_LANDSCAPE_QUERY = '(orientation: landscape) and (max-height: 599px)';

/**
 * Phone held sideways, which is what InputBlockFrame's `landscapeLayout` keys off.
 *
 * COMPOSED from `useIsPhone`, not stated again as a query — and that is the whole point.
 *
 * This used to be the bare `SHORT_LANDSCAPE_QUERY`, and its comment claimed it "matches
 * task-workspace-page.tsx's own `isPhoneLandscape` exactly". That stopped being true the day
 * `PHONE_QUERY` grew its `pointer: coarse` clause to stop a short DESKTOP window being treated
 * as a phone: the fix landed on one predicate and not the other, so a laptop window with
 * devtools docked at the bottom was a phone in landscape and not a phone at the same time. The
 * task workspace then drew its panel toggles and Preview button TWICE, once in the header slot
 * that answers to this and once in the right-hand column that answers to `isPhone`.
 *
 * Composing is what makes the two unable to disagree again. It is also exactly what the query
 * should have said all along: a phone in landscape is a PHONE, held sideways.
 */
export function useIsPhoneLandscape(): boolean {
  const phone = useIsPhone();
  const short = useMediaQuery(SHORT_LANDSCAPE_QUERY);
  return phone && short;
}

/**
 * A pointer that can HOVER — a mouse, not a finger.
 *
 * Distinct from `useIsPhone`, which asks how big the screen is. A control can be worth opening on
 * hover on any sized window that has a mouse, and must never be hover-only where there is none:
 * a finger cannot hover, so anything behind it would be unreachable. Used alongside a press or a
 * key, never instead of one.
 */
export const HOVER_QUERY = '(hover: hover) and (pointer: fine)';

export function useCanHover(): boolean {
  return useMediaQuery(HOVER_QUERY);
}
